import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Home,
    Image as ImageIcon,
    Type,
    Loader2,
    CheckCircle2,
    Building2,
    BarChart3,
    History,
    MessageSquare,
    Heart,
    Trophy,
    MapPin,
    ChevronDown,
    ChevronUp,
    Save,
    Plus,
    Trash2,
    Zap,
    RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInputBase from '../../components/admin/BilingualInput';
import BilingualTextAreaBase from '../../components/admin/BilingualTextArea';

const BilingualInput: React.FC<React.ComponentProps<typeof BilingualInputBase>> = (props) => (
    <BilingualInputBase {...props} showArabic={false} />
);

const BilingualTextArea: React.FC<React.ComponentProps<typeof BilingualTextAreaBase>> = (props) => (
    <BilingualTextAreaBase {...props} showArabic={false} />
);

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({});
    const [expandedSections, setExpandedSections] = useState<string[]>(['Hero Banner', 'Excellence Stats']);
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const saveSectionText = async (sectionName: string) => {
        setSaving(sectionName);
        const sectionData = content[sectionName] || {};

        try {
            const promises = Object.entries(sectionData)
                .filter(([key]) => !key.endsWith('_ar'))
                .map(async ([key, value]: [string, any]) => {
                if (typeof value !== 'string') return;
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')} `
                    },
                    body: JSON.stringify({ 
                    section: sectionName, 
                    content_key: key, 
                    content_value: value,
                    content_value_ar: sectionData[`${key}_ar`] || null
                })
                });

                if (response.status === 401) {
                    localStorage.removeItem('adminToken');
                    navigate('/admin/login');
                    throw new Error('Session expired');
                }

                if (!response.ok) {
                    throw new Error(`Failed to update ${key}`);
                }
            });

            await Promise.all(promises);
            // Optional: Show success toast
        } catch (err) {
            console.error(`Save failed for ${sectionName}`);
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const handleImageSelect = (section: string, key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [`${section}-${key}`]: { file, preview }
        }));
    };

    const saveAllPendingImages = async () => {
        const totalPending = Object.keys(pendingImages).length;
        if (totalPending === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const parts = key.split('-');
                const section = parts[0];
                const keyName = parts.slice(1).join('-');

                const formData = new FormData();
                formData.append('section', section);
                formData.append('content_key', keyName);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')} `
                    },
                    body: formData
                });

                if (response.status === 401) {
                    localStorage.removeItem('adminToken');
                    navigate('/admin/login');
                    return null;
                }

                if (response.ok) {
                    const data = await response.json();
                    return { section, keyName, imageUrl: data.imageUrl };
                } else {
                    const errorData = await response.json();
                    console.error(`Upload failed for ${section} - ${keyName}: `, errorData);
                    return null;
                }
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => {
                const next = { ...prev };
                results.forEach(res => {
                    if (res) {
                        next[res.section] = {
                            ...(next[res.section] || {}),
                            [res.keyName]: res.imageUrl
                        };
                    }
                });
                return next;
            });

            setPendingImages({});
        } catch (err) {
            console.error('Batch upload failed');
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const toggleSection = (name: string) => {
        setExpandedSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
        );
    };

    const UploadBox = ({ section, keyName, label, currentImage }: any) => {
        const isIndividualUploading = saving === `${section}-${keyName}`;
        const isBatchUploading = saving === 'all-images';
        const pending = pendingImages[`${section}-${keyName}`];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[8px] font-black uppercase">Live</span>
                        </div>
                    )}
                    {pending && (
                        <div className="flex items-center gap-1.5 text-amber-500 bg-amber-50 px-2 py-1 rounded-lg">
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                            <span className="text-[8px] font-black uppercase">Pending Save</span>
                        </div>
                    )}
                </div>

                <div className="relative aspect-video bg-slate-50 rounded-2xl overflow-hidden mb-6 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-10 h-10 mb-2 opacity-20" />
                            <span className="text-[10px] font-black uppercase tracking-widest">No Asset Loaded</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold hover:scale-110 transition-transform shadow-2xl flex items-center space-x-3">
                            <ImageIcon className="w-5 h-5" />
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageSelect(section, keyName, file);
                                }}
                            />
                        </label>
                    </div>
                    {(isIndividualUploading || isBatchUploading) && (
                        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3" />
                            <span className="text-[10px] font-black text-blue-600 uppercase">Synchronizing...</span>
                        </div>
                    )}
                </div>
                {(currentImage || pending) && (
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-tighter truncate max-w-[140px]">
                            {pending ? pending.file.name : (currentImage ? currentImage.split('/').pop() : '')}
                        </span>
                        <span className="text-[8px] text-blue-400 font-black">
                            {pending ? `${(pending.file.size / 1024).toFixed(0)} KB` : "Web Optimized"}
                        </span>
                    </div>
                )}
            </div>
        );
    };


    const SectionHeader = ({ title, icon: Icon, sectionKey }: any) => (
        <div className="flex items-center justify-between p-6 bg-white rounded-3xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => toggleSection(title)}>
                <div className={`p-3 rounded-2xl transition-colors ${expandedSections.includes(title) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Section Configuration</p>
                </div>
                {expandedSections.includes(title) ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
            {sectionKey && (
                <button
                    onClick={() => saveSectionText(sectionKey)}
                    disabled={saving === sectionKey}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-emerald-100 active:scale-95"
                >
                    {saving === sectionKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Sync Content</span>
                </button>
            )}
        </div>
    );

    const renderTextContent = () => (
        <div className="space-y-6">
            {/* Hero Section */}
            <SectionHeader title="Hero Banner" icon={Home} sectionKey="Hero" />
            <AnimatePresence>
                {expandedSections.includes('Hero Banner') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            {[...Array(9)].map((_, i) => (
                                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm ml-4 mb-4 space-y-6">
                                    <h3 className="font-bold text-slate-700 mb-2 flex items-center">
                                        <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs mr-2">{i + 1}</span>
                                        Banner Data
                                    </h3>
                                    <BilingualInput 
                                        label="Title"
                                        englishValue={content.Hero?.[`banner_${i + 1}_title`]}
                                        arabicValue={content.Hero?.[`banner_${i + 1}_title_ar`]}
                                        onChangeEnglish={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Hero: { ...(prev.Hero || {}), [`banner_${i + 1}_title`]: val }
                                        }))}
                                        onChangeArabic={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Hero: { ...(prev.Hero || {}), [`banner_${i + 1}_title_ar`]: val }
                                        }))}
                                    />
                                    <BilingualInput 
                                        label="Subtitle"
                                        englishValue={content.Hero?.[`banner_${i + 1}_subtitle`]}
                                        arabicValue={content.Hero?.[`banner_${i + 1}_subtitle_ar`]}
                                        onChangeEnglish={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Hero: { ...(prev.Hero || {}), [`banner_${i + 1}_subtitle`]: val }
                                        }))}
                                        onChangeArabic={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Hero: { ...(prev.Hero || {}), [`banner_${i + 1}_subtitle_ar`]: val }
                                        }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Stats Section */}
            <SectionHeader title="Excellence Stats" icon={BarChart3} sectionKey="Stats" />
            <AnimatePresence>
                {expandedSections.includes('Excellence Stats') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 ml-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                                    <h3 className="font-bold text-slate-700 mb-2">Metric {i}</h3>
                                    <BilingualInput 
                                        label="Count"
                                        englishValue={content.Stats?.[`stat_${i}_value`]}
                                        arabicValue={content.Stats?.[`stat_${i}_value_ar`]}
                                        onChangeEnglish={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Stats: { ...(prev.Stats || {}), [`stat_${i}_value`]: val }
                                        }))}
                                        onChangeArabic={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Stats: { ...(prev.Stats || {}), [`stat_${i}_value_ar`]: val }
                                        }))}
                                    />
                                    <BilingualInput 
                                        label="Label"
                                        englishValue={content.Stats?.[`stat_${i}_label`]}
                                        arabicValue={content.Stats?.[`stat_${i}_label_ar`]}
                                        onChangeEnglish={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Stats: { ...(prev.Stats || {}), [`stat_${i}_label`]: val }
                                        }))}
                                        onChangeArabic={(val) => setContent((prev: any) => ({
                                            ...prev,
                                            Stats: { ...(prev.Stats || {}), [`stat_${i}_label_ar`]: val }
                                        }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Brand Pillars */}
            <SectionHeader title="Brand Pillars" icon={CheckCircle2} sectionKey="Brand Pillars" />
            <AnimatePresence>
                {expandedSections.includes('Brand Pillars') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                                <BilingualInput 
                                    label="Section Title"
                                    englishValue={content['Brand Pillars']?.title}
                                    arabicValue={content['Brand Pillars']?.title_ar}
                                    onChangeEnglish={(val) => setContent((prev: any) => ({
                                        ...prev,
                                        'Brand Pillars': { ...(prev['Brand Pillars'] || {}), title: val }
                                    }))}
                                    onChangeArabic={(val) => setContent((prev: any) => ({
                                        ...prev,
                                        'Brand Pillars': { ...(prev['Brand Pillars'] || {}), title_ar: val }
                                    }))}
                                />
                            </div>
                            <div className="space-y-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
                                        <h3 className="font-bold text-slate-700 mb-2">Pillar {i}</h3>
                                        <BilingualInput 
                                            label="Title"
                                            englishValue={content['Brand Pillars']?.[`pillar_${i}_title`]}
                                            arabicValue={content['Brand Pillars']?.[`pillar_${i}_title_ar`]}
                                            onChangeEnglish={(val) => setContent((prev: any) => ({
                                                ...prev,
                                                'Brand Pillars': { ...(prev['Brand Pillars'] || {}), [`pillar_${i}_title`]: val }
                                            }))}
                                            onChangeArabic={(val) => setContent((prev: any) => ({
                                                ...prev,
                                                'Brand Pillars': { ...(prev['Brand Pillars'] || {}), [`pillar_${i}_title_ar`]: val }
                                            }))}
                                        />
                                        <BilingualTextArea 
                                            label="Description"
                                            englishValue={content['Brand Pillars']?.[`pillar_${i}_desc`]}
                                            arabicValue={content['Brand Pillars']?.[`pillar_${i}_desc_ar`]}
                                            onChangeEnglish={(val) => setContent((prev: any) => ({
                                                ...prev,
                                                'Brand Pillars': { ...(prev['Brand Pillars'] || {}), [`pillar_${i}_desc`]: val }
                                            }))}
                                            onChangeArabic={(val) => setContent((prev: any) => ({
                                                ...prev,
                                                'Brand Pillars': { ...(prev['Brand Pillars'] || {}), [`pillar_${i}_desc_ar`]: val }
                                            }))}
                                            rows={4}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* History Section */}
            <SectionHeader title="Hamdan History" icon={History} sectionKey="History" />
            <AnimatePresence>
                {expandedSections.includes('Hamdan History') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                <BilingualInput 
                                    label="Main Title"
                                    englishValue={content.History?.title}
                                    arabicValue={content.History?.title_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, History: { ...(content.History || {}), title: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, History: { ...(content.History || {}), title_ar: val } })}
                                />
                                <BilingualTextArea 
                                    label="Main Description"
                                    englishValue={content.History?.desc}
                                    arabicValue={content.History?.desc_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, History: { ...(content.History || {}), desc: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, History: { ...(content.History || {}), desc_ar: val } })}
                                    rows={10}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                                        <h3 className="font-bold text-slate-700">Point {i}</h3>
                                        <BilingualInput 
                                            label="Label"
                                            englishValue={content.History?.[`point_${i}_title`]}
                                            arabicValue={content.History?.[`point_${i}_title_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, History: { ...(content.History || {}), [`point_${i}_title`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, History: { ...(content.History || {}), [`point_${i}_title_ar`]: val } })}
                                        />
                                        <BilingualInput 
                                            label="Value/Year"
                                            englishValue={content.History?.[`point_${i}_subtitle`]}
                                            arabicValue={content.History?.[`point_${i}_subtitle_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, History: { ...(content.History || {}), [`point_${i}_subtitle`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, History: { ...(content.History || {}), [`point_${i}_subtitle_ar`]: val } })}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Chairman's Message */}
            <SectionHeader title="Chairman's Message" icon={MessageSquare} sectionKey="Chairman" />
            <AnimatePresence>
                {expandedSections.includes("Chairman's Message") && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                                <BilingualInput 
                                    label="Section Title"
                                    englishValue={content.Chairman?.title}
                                    arabicValue={content.Chairman?.title_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), title: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), title_ar: val } })}
                                />
                                <BilingualTextArea 
                                    label="The Message"
                                    englishValue={content.Chairman?.desc}
                                    arabicValue={content.Chairman?.desc_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), desc: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), desc_ar: val } })}
                                    rows={10}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                                        <h3 className="font-bold text-slate-700">Point {i}</h3>
                                        <BilingualInput 
                                            label="Title"
                                            englishValue={content.Chairman?.[`point_${i}_title`]}
                                            arabicValue={content.Chairman?.[`point_${i}_title_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), [`point_${i}_title`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), [`point_${i}_title_ar`]: val } })}
                                        />
                                        <BilingualInput 
                                            label="Subtitle"
                                            englishValue={content.Chairman?.[`point_${i}_subtitle`]}
                                            arabicValue={content.Chairman?.[`point_${i}_subtitle_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), [`point_${i}_subtitle`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, Chairman: { ...(content.Chairman || {}), [`point_${i}_subtitle_ar`]: val } })}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CSR Section */}
            <SectionHeader title="CSR Initiatives" icon={Heart} sectionKey="CSR" />
            <AnimatePresence>
                {expandedSections.includes('CSR Initiatives') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm mb-4">
                                <BilingualInput 
                                    label="Section Title"
                                    englishValue={content.CSR?.title}
                                    arabicValue={content.CSR?.title_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), title: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), title_ar: val } })}
                                />
                            </div>
                            <div className="space-y-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
                                        <h3 className="font-bold text-slate-700">Point {i}</h3>
                                        <BilingualInput 
                                            label="Title"
                                            englishValue={content.CSR?.[`point_${i}_title`]}
                                            arabicValue={content.CSR?.[`point_${i}_title_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), [`point_${i}_title`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), [`point_${i}_title_ar`]: val } })}
                                        />
                                        <BilingualTextArea 
                                            label="Description"
                                            englishValue={content.CSR?.[`point_${i}_desc`]}
                                            arabicValue={content.CSR?.[`point_${i}_desc_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), [`point_${i}_desc`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, CSR: { ...(content.CSR || {}), [`point_${i}_desc_ar`]: val } })}
                                            rows={4}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Awards Section */}
            <SectionHeader title="Awards & Recognitions" icon={Trophy} sectionKey="Awards" />
            <AnimatePresence>
                {expandedSections.includes('Awards & Recognitions') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm mb-4">
                                <BilingualInput 
                                    label="Section Title"
                                    englishValue={content.Awards?.title}
                                    arabicValue={content.Awards?.title_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), title: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), title_ar: val } })}
                                />
                            </div>
                            <div className="space-y-6">
                                {[1, 2].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
                                        <h3 className="font-bold text-slate-700">Recognition {i}</h3>
                                        <BilingualInput 
                                            label="Title"
                                            englishValue={content.Awards?.[`point_${i}_title`]}
                                            arabicValue={content.Awards?.[`point_${i}_title_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), [`point_${i}_title`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), [`point_${i}_title_ar`]: val } })}
                                        />
                                        <BilingualTextArea 
                                            label="Description"
                                            englishValue={content.Awards?.[`point_${i}_desc`]}
                                            arabicValue={content.Awards?.[`point_${i}_desc_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), [`point_${i}_desc`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, Awards: { ...(content.Awards || {}), [`point_${i}_desc_ar`]: val } })}
                                            rows={4}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Presence Section */}
            <SectionHeader title="Global Presence" icon={MapPin} sectionKey="Presence" />
            <AnimatePresence>
                {expandedSections.includes('Global Presence') && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="space-y-4 mb-8 ml-4">
                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm mb-4">
                                <BilingualInput 
                                    label="Section Title"
                                    englishValue={content.Presence?.title}
                                    arabicValue={content.Presence?.title_ar}
                                    onChangeEnglish={(val) => setContent({ ...content, Presence: { ...(content.Presence || {}), title: val } })}
                                    onChangeArabic={(val) => setContent({ ...content, Presence: { ...(content.Presence || {}), title_ar: val } })}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                                        <h3 className="font-bold text-slate-700 mb-2">Country {i}</h3>
                                        <BilingualInput 
                                            label="Name"
                                            englishValue={content.Presence?.[`country_${i}_name`]}
                                            arabicValue={content.Presence?.[`country_${i}_name_ar`]}
                                            onChangeEnglish={(val) => setContent({ ...content, Presence: { ...(content.Presence || {}), [`country_${i}_name`]: val } })}
                                            onChangeArabic={(val) => setContent({ ...content, Presence: { ...(content.Presence || {}), [`country_${i}_name_ar`]: val } })}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const renderMediaContent = () => {
        return (
            <div className="space-y-12">
                <div className="flex items-center justify-between border-b border-slate-100 pb-8">
                    <div>
                        <h3 className="text-3xl font-black text-slate-800 tracking-tight">Media Assets</h3>
                        <p className="text-slate-500 text-sm font-medium">Manage and deploy high-resolution brand assets</p>
                    </div>
                    <button
                        onClick={saveAllPendingImages}
                        disabled={Object.keys(pendingImages).length === 0 || !!saving}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 active:scale-95 disabled:bg-slate-200"
                    >
                        {saving === 'all-images' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 inline mr-2" />}
                        Push Global Updates
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-16">
                    {/* Hero Section */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                <Home className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Hero Section Banners</h4>
                                <p className="text-[10px] text-slate-400 font-bold">Manage the homepage main slider graphics</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(9)].map((_, i) => (
                                <UploadBox
                                    key={i}
                                    section="Hero"
                                    keyName={`banner_${i + 1}_image`}
                                    label={`Banner Slot ${i + 1}`}
                                    currentImage={content.Hero?.[`banner_${i + 1}_image`]}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Featured backgrounds */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                                <Zap className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Featured Backgrounds</h4>
                                <p className="text-[10px] text-slate-400 font-bold">Core section visual backdrops</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <UploadBox
                                section="History"
                                keyName="bg_image"
                                label="Corporate History BG"
                                currentImage={content.History?.bg_image}
                            />
                            <UploadBox
                                section="Presence"
                                keyName="bg_image"
                                label="Global Logistics Map"
                                currentImage={content.Presence?.bg_image}
                            />
                            <UploadBox
                                section="Chairman"
                                keyName="image_1"
                                label="Founder Signature / Image"
                                currentImage={content.Chairman?.image_1}
                            />
                        </div>
                    </div>

                    {/* Our Presence Country Images */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                                <MapPin className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Our Presence — Country Images</h4>
                                <p className="text-[10px] text-slate-400 font-bold">3 country carousel images on the homepage</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <UploadBox
                                section="Presence"
                                keyName="country_1_image"
                                label="Country 1 Image"
                                currentImage={content.Presence?.country_1_image}
                            />
                            <UploadBox
                                section="Presence"
                                keyName="country_2_image"
                                label="Country 2 Image"
                                currentImage={content.Presence?.country_2_image}
                            />
                            <UploadBox
                                section="Presence"
                                keyName="country_3_image"
                                label="Country 3 Image"
                                currentImage={content.Presence?.country_3_image}
                            />
                        </div>
                    </div>

                    {/* Group Logos */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 border-t border-slate-100 pt-16">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Our Group Logos</h4>
                                <p className="text-[10px] text-slate-400 font-bold">Partner and subsidiary visual identities</p>
                            </div>
                        </div>
                        <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8">
                            {[...Array(8)].map((_, i) => {
                                const key = `logo_${i + 1}_image`;
                                const isUploading = saving === `Group-${key}` || saving === 'all-images';
                                const pending = pendingImages[`Group-${key}`];
                                const displayImage = pending ? pending.preview : content.Group?.[key];

                                return (
                                    <div key={i} className="group flex flex-col items-center">
                                        <div className="relative w-full aspect-square bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center p-4 hover:border-blue-300 hover:shadow-xl transition-all">
                                            {displayImage ? (
                                                <img src={displayImage} className="max-w-full max-h-full object-contain transition-transform group-hover:scale-110" alt={`Logo ${i + 1}`} />
                                            ) : (
                                                <ImageIcon className="w-6 h-6 text-slate-300 opacity-50" />
                                            )}
                                            <label className="absolute inset-0 cursor-pointer opacity-0 group-hover:opacity-100 bg-slate-900/60 flex flex-col items-center justify-center rounded-2xl transition-all p-2 text-center backdrop-blur-[2px]">
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleImageSelect('Group', key, file);
                                                    }}
                                                />
                                                <RefreshCw className="w-5 h-5 text-white mb-1" />
                                                <span className="text-[10px] text-white font-black uppercase tracking-tight">Update</span>
                                            </label>
                                            {isUploading && (
                                                <div className="absolute inset-0 bg-white/90 rounded-2xl flex items-center justify-center">
                                                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                                                </div>
                                            )}
                                            {pending && (
                                                <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-[8px] font-black w-5 h-5 rounded-full flex items-center justify-center shadow-lg animate-pulse border-2 border-white">!</div>
                                            )}
                                        </div>
                                        <span className="mt-4 text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-blue-600 transition-colors">Slot {i + 1}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="h-20" />
            </div>
        );
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Fixed Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Home className="w-6 h-6 text-blue-600" /> Homepage Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Global Admin</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center font-bold text-white border border-slate-700 shadow-inner">
                            {username[0]}
                        </div>
                    </div>
                </header>

                {/* Scrollable Content Area */}
                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-40">
                            <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                            <p className="text-slate-400 font-black uppercase tracking-widest text-[10px]">Synchronizing Repository...</p>
                        </div>
                    ) : (
                        <div className="max-w-6xl mx-auto">
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div
                                        key="text-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-6"
                                    >
                                        <div className="flex items-center justify-between mb-10">
                                            <div>
                                                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Homepage Editor</h1>
                                                <p className="text-slate-500 font-medium mt-1">Reflect changes instantly on your public website.</p>
                                            </div>
                                            <button
                                                onClick={fetchContent}
                                                className="flex items-center space-x-2 p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                            >
                                                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                                                <span className="font-bold text-xs uppercase tracking-widest px-1">Sync</span>
                                            </button>
                                        </div>
                                        {renderTextContent()}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="media-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-12"
                                    >
                                        {renderMediaContent()}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </main>
            </div>


        </div>
    );
};

export default AdminDashboard;

